import {
  Cartesian3,
  Cartographic,
  Math as CesiumMath,
  Color,
  ColorGeometryInstanceAttribute,
  DistanceDisplayCondition,
  DistanceDisplayConditionGeometryInstanceAttribute,
  EllipsoidTerrainProvider,
  GeometryInstance,
  Material,
  MaterialAppearance,
  PolygonGeometry,
  PolylineDashMaterialProperty,
  PolylineGeometry,
  PolylineMaterialAppearance,
  Primitive,
  Rectangle,
  ShowGeometryInstanceAttribute,
  SceneTransforms,
  defined,
  createPropertyDescriptor,
  ConstantPositionProperty,
  JulianDate,
  Cartesian2,
  PolygonHierarchy,
} from "@cesium/engine";
import DitapEntity from "../object/DitapEntity";

const Utils = {
  /**
   * 프로토콜 조회.<br>
   * Protocol query.
   * @returns {string} 프로토콜
   */
  getProtocol() {
    return window.location.protocol;
  },

  /**
   * 모바일/PC 기기 구분
   * @returns {boolean} 모바일 여부
   */
  detectDeviceType() {
    let isMobile = false;
    // 화면 너비 및 높이 체크
    const screenWidth = window.innerWidth;

    if (screenWidth <= 1200) {
      isMobile = true;
    } else {
      isMobile = false;

      const isTouchDevice = "ontouchstart" in document.documentElement;
      isMobile = isTouchDevice;
    }

    return isMobile;
  },

  /**
   * 디바이스가 터치가 되는지 확인
   * @returns {boolean} 터치 여부
   */
  isTouchDevice() {
    const isTouchDevice = "ontouchstart" in document.documentElement;
    return isTouchDevice;
  },

  /**
   * body요소의 width가 600 이하인지 구분
   * @returns {boolean} 600이하 여부
   */
  isBodyWidthLessThan600() {
    const bodyWidth = document.body.clientWidth;
    return bodyWidth <= 600;
  },

  /**
   * body요소의 width가 1200 이하인지 구분
   * @returns {boolean} 1200이하 여부
   */
  isBodyWidthLessThan1200() {
    const bodyWidth = document.body.clientWidth;
    return bodyWidth <= 1200;
  },

  /**
   * body요소의 width반환.
   * @returns {number} body요소의 width
   */
  getBodyWidth() {
    return document.body.clientWidth;
  },

  /*
   * 컬러 픽커 모바일, 테블릿 width 설정
   */
  getColorPickerWidth() {
    return Utils.isBodyWidthLessThan1200()
      ? Utils.isBodyWidthLessThan600()
        ? Utils.getBodyWidth() - 100
        : 253
      : 307;
  },

  /**
   * 포인트 엔티티 색상 변경.<br>
   * Change the color of the point entities.
   * @param {Entity[]} pointEntities 색상 변경할 포인트 엔티티 배열.<br> Array of point entities to change the color.
   * @param {Color} pointColor 변경할 색상.<br> The color to change to.
   */
  changePointColor(pointEntities, pointColor) {
    if (Array.isArray(pointEntities)) {
      // 배열인 경우
      pointEntities.forEach((entity) => {
        if (entity.point) {
          entity.point.outlineColor = pointColor;
        }
      });
    } else if (pointEntities.point) {
      // 개별 객체인 경우
      pointEntities.point.outlineColor = pointColor;
    }
  },

  /**
   * 폴리라인 엔티티 색상 변경.<br>
   * Change the color of the polyline entities.
   * @param {Entity[]} polylineEntities 색상 변경할 폴리라인 엔티티 배열.<br> Array of polyline entities to change the color.
   * @param {Color | MaterialProperty} polylineColor 변경할 색상.<br> The color to change to.
   */
  changePolylineColor(polylineEntities, polylineColor) {
    const changeColor = (polyline, polylineColor) => {
      if (defined(polyline) && defined(polyline.material)) {
        let depthFailColor;
        if (polylineColor instanceof Color) {
          depthFailColor = polylineColor.withAlpha(0.5);
        } else {
          depthFailColor = polylineColor.color.getValue().withAlpha(0.5);
        }

        polyline.material = polylineColor;
        polyline.depthFailMaterial = new PolylineDashMaterialProperty({
          color: depthFailColor,
          dashLength: 10,
        });
      }
    };

    if (Array.isArray(polylineEntities)) {
      polylineEntities.forEach((entity) => {
        changeColor(entity.polyline, polylineColor);
      });
    } else if (polylineEntities.polyline) {
      changeColor(polylineEntities.polyline, polylineColor);
    }
  },

  /**
   * 폴리곤 엔티티 색상 변경.<br>
   * Change the color of the polygon entities.
   * @param {Entity[]} polygonEntities 색상 변경할 폴리곤 엔티티 배열.<br> Array of polygon entities to change the color.
   * @param {Color } polygonColor 변경할 색상.<br> The color to change to.
   */
  changePolygonPrimitiveColor(polygonPrimtivies, polygonColor) {
    polygonPrimtivies.forEach((primitive) => {
      if (defined(primitive?.appearance?.material?.uniforms?.color)) {
        primitive.appearance.material.uniforms.color = polygonColor.withAlpha(
          0.3
        );
      }
    });
  },

  findMinMaxCoordinates(positions) {
    // 초기값 설정
    let minX = Number.MAX_VALUE;
    let maxX = -Number.MAX_VALUE;
    let minY = Number.MAX_VALUE;
    let maxY = -Number.MAX_VALUE;

    // positions 배열을 순회하며 최소 및 최대값 갱신
    positions.forEach((position) => {
      minX = Math.min(minX, position.x);
      maxX = Math.max(maxX, position.x);
      minY = Math.min(minY, position.y);
      maxY = Math.max(maxY, position.y);
    });

    return { minX, maxX, minY, maxY };
  },

  getTextureCoordinates(positions) {
    const { minX, maxX, minY, maxY } = this.findMinMaxCoordinates(positions);
    // textureCoordinates 계산
    let textureCoordinates;
    if (positions.length === 4) {
      textureCoordinates = [
        new Cartesian2(0, 1),
        new Cartesian2(0, 0),
        new Cartesian2(1, 0),
        new Cartesian2(1, 1),
      ];
    } else {
      textureCoordinates = positions.map((position) => {
        // positions의 Cartesian3 좌표를 [0, 1] 범위로 정규화하여 텍스처 좌표로 변환
        const x = 1 - (position.x - minX) / (maxX - minX);
        const y = 1 - (position.y - minY) / (maxY - minY);
        return new Cartesian2(x, y);
      });
    }

    return textureCoordinates;
  },

  /**
   * DitapViewer에 PolygonPrimitive생성. </br>
   * Create a PolygonPrimitive in the viewer container.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {String} id PolygonPrimitive의 Id.</br>The ID of the PolygonPrimitive.
   * @param {number} displayCondition PolygonPrimitive을 표시할 거리 조건.</br>Distance condition for displaying PolygonPrimitive.
   * @param {Cartesian3[]} positions PolygonPrimitive 좌표 배열.</br> Cartesians of PolygonPrimitive.
   * @param {MaterialProperty | Color} material PolygonPrimitive의 색상 또는 재질.</br> A Property specifying the material used to draw the PolygonPrimitive.
   * @returns {Entity} 생성된 PolygonPrimivie.</br>Created PolygonPrimivie.
   */
  createPolygonPrimitive(polygonOption) {
    if (!polygonOption || !polygonOption.viewer) {
      return;
    }

    const polyGeometry = new PolygonGeometry({
      polygonHierarchy: new PolygonHierarchy(polygonOption.positions),
      perPositionHeight: true,
      textureCoordinates: {
        positions: this.getTextureCoordinates(polygonOption.positions),
      },
    });

    const polyInstance = new GeometryInstance({
      geometry: polyGeometry,
      id: polygonOption.id,
      attributes: {
        // color: ColorGeometryInstanceAttribute.fromColor(polygonOption.material),
        depthFailColor: ColorGeometryInstanceAttribute.fromColor(
          Color.GRAY.withAlpha(0.3)
        ),
        distanceDisplayCondition: new DistanceDisplayConditionGeometryInstanceAttribute(
          0,
          polygonOption.displayCondition
        ),
        show: new ShowGeometryInstanceAttribute(true),
      },
    });

    const appearance = new MaterialAppearance({
      material: new Material({
        fabric: {
          type: "Color",
          uniforms: {
            color: polygonOption.material,
          },
        },
      }),
    });
    const depthFailAppearance = new MaterialAppearance({
      material: new Material({
        fabric: {
          type: "Color",
          uniforms: {
            color: polygonOption.material.withAlpha(0.5),
          },
        },
      }),
    });

    const primitive = polygonOption.viewer.scene.primitives.add(
      new Primitive({
        geometryInstances: polyInstance,
        appearance: appearance,
        depthFailAppearance: depthFailAppearance,
        releaseGeometryInstances: false,
      })
    );

    return primitive;
  },

  /**
   * DitapViewer에 PolylinePrimitive생성. </br>
   * Create a PolylinePrimitive in the viewer container.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {String} id PolylinePrimitive Id.</br>The ID of the PolylinePrimitive.
   * @param {Cartesian3[]} positions PolylinePrimitive 좌표 배열.</br> Cartesians of PolylinePrimitive.
   * @param {MaterialProperty | Color} material PolylinePrimitive 색상 또는 재질.</br> A Property specifying the material used to draw the PolylinePrimitive.
   * @returns {Entity} 생성된 PolygonPrimivie.</br>Created PolylinePrimitive.
   */
  createPolylinePrimitive(polylineOption) {
    if (!polylineOption || !polylineOption.viewer) {
      return;
    }
    const polyline = new PolylineGeometry({
      positions: polylineOption.positions,
      width: 3,
    });

    // Create a primitive using the PolylineGeometry instance
    const primitive = new Primitive({
      geometryInstances: new GeometryInstance({
        geometry: polyline,
        attributes: {
          color: ColorGeometryInstanceAttribute.fromColor(Color.WHITE),
        },
      }),
      appearance: new PolylineMaterialAppearance({
        material: polylineOption.material
          ? polylineOption.material
          : Material.fromType("Color", {
              color: Color.WHITE,
            }),
      }),
      allowPicking: false,
    });

    // Add the primitive to the scene
    polylineOption.viewer.scene.primitives.add(primitive);
    return primitive;
  },

  /**
   * 폴리곤 엔티티 생성
   * @param {object} polygonOption 폴리곤 옵션.
   * @param {string} polygonOption.id 아이디.
   * @param {DitapViewer} polygonOption.viewer 사용할 Ditap Viewer 인스턴스
   * @param {Cartesian3[]} polygonOption.positions 좌표 배열.
   * @param {MaterialProperty | Color} polygonOption.material 폴리곤 재질.
   * @returns {Entity} polygon 폴리곤 엔티티.
   */
  createPolygon(polygonOption) {
    if (!polygonOption || !polygonOption.viewer) {
      return;
    }

    const polygon = new DitapEntity({
      id: polygonOption.id || undefined,
      polygon: {
        show: true,
        hierarchy: polygonOption.positions,
        material: polygonOption.material || Color.WHITE.withAlpha(0.7),
        perPositionHeight: true,
      },
    });

    polygonOption.viewer.entities.add(polygon);

    return polygon;
  },

  /**
   * 라인 엔티티 생성
   * @param {object} polylineOption 라인 옵션.
   * @param {DitapViewer} polylineOption.viewer 사용할 Ditap Viewer 인스턴스
   * @param {string} polylineOption.id 아이디.
   * @param {Cartesian3[]} polylineOption.positions 좌표 배열.
   * @param {GridMaterialProperty | Color} polylineOption.material 라인 재질.
   * @param {Number} polylineOption.displayCondition 가시화 거리.
   * @returns {Entity} polyline 라인 엔티티.
   */
  createPolyline(polylineOption) {
    if (!polylineOption || !polylineOption.viewer) {
      return;
    }

    const polyline = new DitapEntity({
      id: polylineOption.id || undefined,
      polyline: {
        show: true,
        positions: polylineOption.positions,
        width: 3,
        material: polylineOption.material || Color.WHITE,
        depthFailMaterial: polylineOption.depthFailMaterial
          ? polylineOption.depthFailMaterial
          : polylineOption.material?.withAlpha
          ? new PolylineDashMaterialProperty({
              color: polylineOption.material.withAlpha(0.5),
              dashLength: 10,
            })
          : Color.GRAY,
        distanceDisplayCondition: new DistanceDisplayCondition(
          0,
          polylineOption.displayCondition || Number.MAX_VALUE
        ),
      },
    });

    polylineOption.viewer.entities.add(polyline);

    return polyline;
  },

  /**
   * 포인트 엔티티 생성
   * @param {object} pointOption 포인트 옵션.
   * @param {DitapViewer} pointOption.viewer 사용할 Ditap Viewer 인스턴스
   * @param {string} pointOption.id 아이디.
   * @param {Cartesian3} pointOption.position 좌표 배열.
   * @param {Color} pointOption.color 포인트 색상.
   * @param {Color} pointOption.outlineColor 포인트 아웃라인 색상.
   * @param {Number} pointOption.displayCondition 가시화 거리.
   * @param {Property|number} pointOption.scale 포인트 사이즈
   * @param {function} pointOption.changedCallback 포인트 값 변경 콜백
   * @returns {Entity} point 포인트 엔티티.
   */
  createPoint(pointOption) {
    if (!pointOption || !pointOption.viewer) {
      return;
    }
    const point = new DitapEntity({
      id: pointOption.id || undefined,
      position: pointOption.position,
      scale: pointOption.scale,
      point: {
        show: true,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        distanceDisplayCondition: new DistanceDisplayCondition(
          0,
          pointOption.displayCondition || Number.MAX_VALUE
        ),
        color: pointOption.color || Color.WHITE,
        pixelSize: pointOption.pixelSize || 8,
        eyeOffset: new Cartesian3(0, 0, -10),
        outlineColor: pointOption.outlineColor || Color.GRAY,
        outlineWidth: 3,
      },
      billboard: {
        url: undefined,
        show: false,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        distanceDisplayCondition: new DistanceDisplayCondition(
          0,
          pointOption.displayCondition || Number.MAX_VALUE
        ),
      },
    });

    // point changed callback
    if (typeof pointOption.changedCallback === "function") {
      point._definitionChanged.addEventListener(pointOption.changedCallback);
    }

    pointOption.viewer.entities.add(point);
    return point;
  },

  /*
   * Ditap Viewer에 도움말 라벨 생성. </br>
   * Create a info-label element in the viewer container.
   * @returns {Element} 생성된 라벨 요소. </br> Elements created to the container.
   */
  createInfoLabel(container, text1, text2, isEnd, isRemove) {
    const div1 = document.createElement("div");

    const div2 = document.createElement("div");
    const p = document.createElement("p");
    div1.classList.add("ditap-card");
    div1.classList.add("ditap-card-txt");
    div1.classList.add("js-ditap-label");

    div2.classList.add("ditap-card-title");
    p.textContent = text1;
    p.classList.add("ditap-padding-reset");
    div2.appendChild(p);

    if (text2) {
      const p2 = document.createElement("p");
      p2.textContent = text2;
      p2.classList.add("ditap-padding-reset");
      div2.appendChild(p2);
    }

    div1.appendChild(div2);

    if (isEnd) {
      const div3 = document.createElement("div");
      const p3 = document.createElement("p");
      const p4 = document.createElement("p");
      div3.classList.add("ditap-property-item");
      div3.classList.add("property-yellow-dot");
      p3.classList.add("ditap-property-key");
      p3.textContent = "완료";
      p4.classList.add("ditap-property-value");
      p4.textContent = "더블 클릭";
      div3.appendChild(p3);
      div3.appendChild(p4);
      div1.appendChild(div3);
    }

    if (isRemove) {
      // 한 점 제거
      const div4 = document.createElement("div");
      const p5 = document.createElement("p");
      const p6 = document.createElement("p");
      div4.classList.add("ditap-property-item");
      div4.classList.add("property-green-dot");
      p5.classList.add("ditap-property-key");
      p5.textContent = "한 점 제거";
      p6.classList.add("ditap-property-value");
      p6.textContent = "오른 클릭 또는 백 스페이스";
      div4.appendChild(p5);
      div4.appendChild(p6);
      div1.appendChild(div4);
    }

    // 취소
    const div5 = document.createElement("div");
    const p7 = document.createElement("p");
    const p8 = document.createElement("p");
    div5.classList.add("ditap-property-item");
    div5.classList.add("property-red-dot");
    p7.classList.add("ditap-property-key");
    p7.textContent = "취소";
    p8.classList.add("ditap-property-value");
    p8.textContent = "오른 클릭 또는 ESC";
    div5.appendChild(p7);
    div5.appendChild(p8);
    div1.appendChild(div5);

    //도움말 표시
    div1.style.right = "5000px";
    div1.addEventListener("mouseover", () => {
      div1.classList.add("hide");
    });

    container.appendChild(div1);
    return div1;
  },

  /*
   * Ditap Viewer에 도움말 라벨 생성. </br>
   * Create a info-label element in the viewer container.
   * @returns {Element} 생성된 라벨 요소. </br> Elements created to the container.
   */
  createMobileInfoLabel(container, text1, text2, isEnd, isRemove) {
    const labelContainer = document.createElement("div");
    labelContainer.classList.add("ditap-mobile-label");

    const textContainer = document.createElement("div");
    textContainer.classList.add("ditap-mobile-label-txt");
    const warningWrap = document.createElement("p");
    warningWrap.classList.add("ditap-mobile-label-icon-wrap");
    const warningIcon = document.createElement("p");
    warningIcon.classList.add("ditap-mobile-label-icon");
    warningWrap.append(warningIcon);
    textContainer.appendChild(warningIcon);
    const textP1 = document.createElement("p");
    textP1.classList.add("ditap-mobile-label-text1");

    if (container.querySelector(".ditap-search")) {
      if (container.classList.contains("ditap-panel-open")) {
        labelContainer.classList.add("bottom-2");
      } else {
        labelContainer.classList.add("bottom-1");
      }
    } else if (container.classList.contains("ditap-panel-open")) {
      labelContainer.classList.add("bottom-1");
    }

    if (text2) {
      textP1.innerHTML = `${text1}</br> ${text2}`;
    } else {
      textP1.textContent = text1;
    }

    textContainer.appendChild(textP1);

    // if (text2) {
    //   const textP2 = document.createElement("p");
    //   textP2.classList.add("ditap-minfo-label-text2");
    //   textP2.textContent = text2;
    //   textP2.classList.add("ditap-padding-reset");
    //   textContainer.appendChild(textP2);
    // }

    labelContainer.append(textContainer);

    const btnContainer = document.createElement("div");
    btnContainer.classList.add("ditap-btn-wrap");
    btnContainer.style.display = "flex";

    let endContainer;
    if (isEnd) {
      endContainer = document.createElement("button");
      endContainer.classList.add(
        "ditap-mobile-label-btn-wrap",
        "ditap-custom-button"
      );
      const icon = document.createElement("p");
      icon.classList.add("icon", "ditap-mobile-label-end");
      const endBtn = document.createElement("p");
      endBtn.classList.add("ditap-label-btn");
      endBtn.textContent = "완료";
      endContainer.appendChild(icon);
      endContainer.appendChild(endBtn);
      btnContainer.appendChild(endContainer);
    }

    let removeContainer;
    if (isRemove) {
      removeContainer = document.createElement("button");
      const icon = document.createElement("p");
      icon.classList.add("icon", "ditap-mobile-label-remove");
      const removeBtn = document.createElement("p");
      removeBtn.classList.add("ditap-label-btn");
      removeBtn.textContent = "한 점 제거";
      removeContainer.classList.add(
        "ditap-mobile-label-btn-wrap",
        "ditap-custom-button",
        "big"
      );
      removeContainer.appendChild(icon);
      removeContainer.appendChild(removeBtn);
      btnContainer.appendChild(removeContainer);
    }

    const cancelContainer = document.createElement("button");
    cancelContainer.classList.add(
      "ditap-mobile-label-btn-wrap",
      "ditap-custom-button"
    );
    const icon = document.createElement("p");
    icon.classList.add("icon", "ditap-mobile-label-cancel");
    const cancelBtn = document.createElement("p");
    cancelBtn.classList.add("ditap-label-btn");
    cancelBtn.textContent = "취소";
    cancelContainer.appendChild(icon);
    cancelContainer.appendChild(cancelBtn);
    btnContainer.appendChild(cancelContainer);

    labelContainer.appendChild(btnContainer);
    //도움말 표시

    container.appendChild(labelContainer);

    this.moveUnderLabel(container);
    return {
      cancelBtn: cancelContainer,
      endBtn: endContainer,
      removeBtn: removeContainer,
      label: labelContainer,
    };
  },

  getPopupMinTop(container) {
    if (container.offsetWidth <= 900) {
      return 180;
    } else if (container.offsetWidth > 900) {
      return 110;
    }
    return 0;
  },

  moveUnderLabel(container) {
    let height = this.getPopupMinTop(container);

    const mobileLabel = container.getElementsByClassName("ditap-mobile-label");
    // 터치 기기이면서 해상도 600이상인 경우에만 라벨 위치 변경.
    if (this.detectDeviceType() && window.innerWidth > 600) {
      const popupElements = document.getElementsByClassName("draggable");

      // 가져온 모든 요소에 대해 반복합니다.
      for (let i = 0; i < popupElements.length; i++) {
        const popupElement = popupElements[i];

        const currentX = popupElement.style.left
          ? parseFloat(popupElement.style.left)
          : 0;
        const currentY =
          popupElement.style.top && popupElement.style.top !== "unset"
            ? parseFloat(popupElement.style.top)
            : 0;

        if (height < currentY) {
          height = currentY;
        }

        // popupElement.style.transform = `translate(${currentX}px, ${height}px)`;
        popupElement.style.left = `${currentX}px`;
        if (mobileLabel.length > 0) {
          popupElement.style.top = `${height}px`;
        } else {
          popupElement.style.top = `${currentY}px`;
        }
      }
    }
  },

  /*
   * 'js-ditap-label' 클래스를 가진 모든 엔티티를 숨기기
   */
  hideLabelElement() {
    const labelElement = document.querySelectorAll(".js-ditap-label");
    labelElement.forEach(function (entity) {
      entity.classList.add("hide");
    });
  },

  /*
   * 'js-ditap-label' 클래스를 가진 모든 엔티티를 다시 보이게 하기
   */
  showLabelElement() {
    const labelElement = document.querySelectorAll(".js-ditap-label");
    labelElement.forEach(function (entity) {
      entity.classList.remove("hide");
    });
  },

  /*
   * Ditap Viewer에 길이/면적 라벨 생성. </br>
   * Create length/area labels in the Ditap viewer.
   * @param {number} length 라벨에 표시 될 길이. </br> Length to be displayed on the label.
   * @param {boolean} isArea 면적 라벨 여부.
   * @returns {Element} 생성된 라벨 요소. </br> Elements created to the container.
   */
  createTotalLabel(viewer, text, isArea) {
    text = Utils.printCommaSeparator(text);
    const div = document.createElement("div");
    const p = document.createElement("p");
    div.classList.add("ditap-card", "ditap-card-value", "total");
    p.classList.add("ditap-card-title");
    if (isArea) {
      p.textContent = `${text}m²`;
    } else {
      p.textContent = `${text}m`;
    }
    div.appendChild(p);

    viewer.container.querySelector(".cesium-viewer").append(div);

    div.addEventListener("mouseover", this.hideLabelElement);
    div.addEventListener("mouseout", this.showLabelElement);
    return div;
  },

  /*
   * Ditap Viewer에 개별 길이 라벨 생성. </br>
   * Create a individual length label element in the viewer container.
   * @param {number} length 라벨에 표시 될 길이. </br> Length to be displayed on the label.
   * @param {number} totalLength 라벨에 표시 될 전체 길이.</br> Total length to be displayed on the label.
   * @returns {Element} 생성된 라벨 요소. </br> Elements created to the container.
   */
  createLabel(viewer, length, totalLength) {
    length = Utils.printCommaSeparator(length);
    totalLength = Utils.printCommaSeparator(totalLength);

    const div = document.createElement("div");
    div.classList.add("ditap-card");
    div.classList.add("ditap-distance");

    const div2 = document.createElement("div");
    div2.classList.add("ditap-distance-item");

    const div3 = document.createElement("div");
    div3.classList.add("ditap-distance-section");
    const p = document.createElement("p");
    p.classList.add("ditap-distance-img");
    const p2 = document.createElement("p");
    p2.classList.add("ditap-distance-title");
    p2.textContent = "구간";

    const p3 = document.createElement("p");
    p3.classList.add("ditap-distance-value");
    p3.textContent = `${length}m`;
    div3.appendChild(p);
    div3.appendChild(p2);

    div2.appendChild(div3);
    div2.appendChild(p3);

    const div4 = document.createElement("div");
    div4.classList.add("ditap-distance-item");
    const div5 = document.createElement("div");
    div5.classList.add("ditap-distance-total");
    const p4 = document.createElement("p");
    p4.classList.add("ditap-distance-img");
    const p5 = document.createElement("p");
    p5.classList.add("ditap-distance-title");
    p5.textContent = "총합";

    const p6 = document.createElement("p");
    p6.classList.add("ditap-distance-value");
    p6.textContent = `${totalLength}m`;

    div5.appendChild(p4);
    div5.appendChild(p5);

    div4.appendChild(div5);
    div4.appendChild(p6);

    div.appendChild(div2);
    div.appendChild(div4);

    div.addEventListener("mouseover", this.hideLabelElement);
    div.addEventListener("mouseout", this.showLabelElement);
    viewer.container.querySelector(".cesium-viewer").append(div);
    return div;
  },

  /*
   * Cartesian 값을 윈도우 좌표로 변환하여 라벨 위치 변경.</br>
   * Change the label position by converting the Cartesian value to window coordinates.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {Cartesian3} position 윈도우 좌표로 변환 할 Cartesian 좌표.</br> Cartesian value to change to window coordinates.
   * @param {Element} label 위치 변경 할 라벨.</br> Label element to change location.
   * @param {Number} labelHeight 라벨 높이</br> Height of the label
   */
  changeLabelLocation(viewer, position, label, labelHeight) {
    const windowPosition = SceneTransforms.wgs84ToWindowCoordinates(
      viewer.scene,
      position
    );

    if (windowPosition) {
      if (labelHeight) {
        label.style.left = `${windowPosition.x + 5}px`;
        label.style.top = `${windowPosition.y - labelHeight - 5}px`;
      } else {
        label.style.left = `${windowPosition.x + 20}px`;
        label.style.top = `${windowPosition.y + 20}px`;
      }
      label.classList.remove("hide");
      label.style.right = "unset";
    } else {
      label.classList.add("hide");
    }
  },

  /*
   * 주어진 위치의 토탈 라벨 가시성 업데이트
   * @param {Cartesian3} position - 업데이트할 라벨의 위치
   * @param {HTMLElement} label - 업데이트할 라벨 요소
   * @param {number} height - 라벨의 높이
   * @param {number} displayCondition - 라벨을 표시할 거리 조건
   */
  changeTotalLabelVisibility(
    viewer,
    label,
    position,
    displayCondition,
    height
  ) {
    const windowPosition = SceneTransforms.wgs84ToWindowCoordinates(
      viewer.scene,
      position
    );

    console.log(position);

    const cameraPosition = viewer.camera.position;
    const distanceToCamera = Cartesian3.distance(cameraPosition, position);

    if (windowPosition) {
      // ditapContainer의 width, height보다 큰 경우는 라벨 안보이게
      if (
        viewer.container.offsetWidth > windowPosition.x &&
        viewer.container.offsetHeight > windowPosition.y &&
        windowPosition.x > 0 &&
        windowPosition.y > 0 &&
        distanceToCamera < displayCondition
      ) {
        label.classList.remove("hide");
        // label.style.left = `${windowPosition.x + viewer.container.offsetLeft + 5}px`;
        // label.style.top = `${windowPosition.y + viewer.container.offsetTop - height - 5}px`;
        label.style.left = `${windowPosition.x + 5}px`;
        label.style.top = `${windowPosition.y - height - 5}px`;
      } else {
        label.classList.add("hide");
      }
    }
  },

  /*
   * 주어진 위치의 라벨 가시성 업데이트
   * @param {Cartesian3} position - 업데이트할 라벨의 위치
   * @param {HTMLElement} label - 업데이트할 라벨 요소
   * @param {number} height - 라벨의 높이
   * @param {number} displayCondition - 라벨을 표시할 거리 조건
   */
  changeLabelVisibility(viewer, labels, positions, displayCondition, height) {
    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      const position = positions[i];

      this.changeTotalLabelVisibility(
        viewer,
        label,
        position,
        displayCondition,
        height
      );
    }
  },

  /*
   * Polyline Cartesian 배열에서 중심 계산.</br>
   * Calculate center cartesians among polyline cartesians.
   * @param {Cartesian3[]} positions 중심 계산할 Cartesian 배열.</br> Polyline cartesian array.
   * @returns {Cartesian3} 계산된 중심 좌표.</br>  Calculated center cartesian.
   */
  getCenterPosition(positions) {
    let centerIndex; // 폴리라인 중심점 인덱스
    const cartesiansList = []; // lerp 배열
    for (let i = 0; i < positions.length - 1; i++) {
      const distance = Cartesian3.distance(positions[i], positions[i + 1]);
      const count = Math.ceil(distance);

      const cartesiansArray = new Array(count);
      for (let j = 0; j < count; ++j) {
        let offset;
        if (count <= 1) {
          offset = j;
        } else {
          offset = j / (count - 1);
        }
        cartesiansArray[j] = Cartesian3.lerp(
          positions[i],
          positions[i + 1],
          offset,
          new Cartesian3()
        );
      }
      cartesiansList.push(cartesiansArray);
    }

    const totalList = [];
    for (let i = 0; i < cartesiansList.length; i++) {
      for (let j = 0; j < cartesiansList[i].length; j++) {
        totalList.push(cartesiansList[i][j]);
      }
    }

    const arrayLength = totalList.length;

    if (arrayLength / 2 === 0) {
      centerIndex = arrayLength % 2;
    } else {
      centerIndex = Math.floor(arrayLength / 2);
    }

    return totalList[centerIndex];
  },

  /*
   * Terrain provider 여부에 따라 새로운 Cartesian 반환.</br>
   * Returns new cartesian based on ellipsoid terrain provider presence.
   * @param {Cartesian3} position 재구성할 Cartesian 좌표.</br> The cartesian to be reconstructed.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @return {Cartesian3} 재구성된 Cartesian 좌표. </br> Reconstructed based on ellipsoid terrain provider presence.
   */
  pickPosition(position, viewer) {
    let newPosition;
    let isEllipsoid = false;

    if (viewer.terrainProvider instanceof EllipsoidTerrainProvider) {
      isEllipsoid = true;
    }

    if (!isEllipsoid) {
      newPosition = viewer.scene.pickPosition(position);
    } else {
      const picked = viewer.scene.pick(position);
      if (defined(picked)) {
        newPosition = viewer.scene.pickPosition(position);
      } else {
        newPosition = viewer.camera.pickEllipsoid(
          position,
          viewer.scene.globe.ellipsoid
        );
      }
    }

    return newPosition;
  },

  /**
   * 두 좌표가 동일한지 비교(5mm 이하인 경우 같다고 판단).<br>
   * Compare if two coordinates are the same (considered the same if within 5mm).
   * @param {Cartesian3} position 동일 여부를 판단할 첫 번째 위치.<br> The first position to determine if they are the same.
   * @param {Cartesian3} position2 동일 여부를 판단할 두 번째 위치.<br> The second position to determine if they are the same.
   * @returns {boolean} 좌표 동일 여부.<br> Whether the two coordinates are identical.
   */
  isEqualPosition(position, position2) {
    const tolerance = 0.005; // 0.005m === 5mm
    let flag = false;

    if (defined(position) && defined(position2)) {
      // 두 좌표 사이의 거리를 계산
      const distance = Cartesian3.distance(position2, position);
      flag = distance.toFixed(2) < tolerance;
    }
    return flag;
  },

  /**
   * base64Data 이미지를 Url로 변환.</br>
   * Convert base64Data image to url.
   * @param {string} base64Data 변환 시킬 base64Data 이미지.</br> The image to convert.
   * @returns {URL} 변환된 url.</br> The image url.
   */
  getUrlFromBase64(base64Data) {
    // * url - base64Data 이미지를 url반환
    const b64Data = base64Data;
    const byteCharacters = window.atob(b64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "image/png" });

    return URL.createObjectURL(blob);
  },

  /**
   * 숫자에 세 자리마다 콤마(,) 입력해서 반환
   * @param {number} numberParam ',' 입력할 숫자.<br> "," Enter the number to input.
   * @returns {number} ',' 입력된 숫자. <br> The entered number.
   */
  printCommaSeparator(numberParam) {
    // 문자로 변환
    let asStr = numberParam.toString();
    // 숫자로 변환
    const asFloat = parseFloat(numberParam);
    let underPoint = "";
    if (asStr.indexOf(".") !== -1 && !isNaN(asFloat)) {
      // float형임
      const splt = asStr.split(".");
      if (splt.length !== 2) {
        console.error("숫자 형식 오류");
        return;
      }
      const overPoint = splt[0];
      underPoint = splt[1];

      asStr = overPoint.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      asStr = `${asStr}.${underPoint}`;
    } else if (asStr.indexOf(".") === -1 && !isNaN(asFloat)) {
      // int형임
      asStr = asStr.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    return asStr;
  },

  /**
   * Cartesian 배열로 bbox를 가져와 distanceDisplayCondition값 반환.<br>
   * Get bbox with Cartesian array and return distanceDisplaycondition value
   * @param {Cartesian3[]} positions bbox를 구할 Cartesian 배열.<br> Cartesian array to find a bbox
   * @returns {number} distanceDisplaycondition 값. <br> distanceDisplaycondition value
   */
  getDistanceDisplayConditon(positions) {
    if (positions.length === 1) {
      return 0;
    }

    const cartographic1 = Cartographic.fromCartesian(positions[0]);
    const cartographic2 = Cartographic.fromCartesian(positions[1]);

    const latitude1 = CesiumMath.toDegrees(cartographic1.latitude);
    const longitude1 = CesiumMath.toDegrees(cartographic1.longitude);
    const latitude2 = CesiumMath.toDegrees(cartographic2.latitude);
    const longitude2 = CesiumMath.toDegrees(cartographic2.longitude);

    let minLongitude = Math.min(longitude1, longitude2);
    let maxLongitude = Math.max(longitude1, longitude2);
    let minLatitude = Math.min(latitude1, latitude2);
    let maxLatitude = Math.max(latitude1, latitude2);

    if (positions.length > 2) {
      for (let i = 1; i < positions.length; i++) {
        const cartographic = Cartographic.fromCartesian(positions[i]);

        const latitude = CesiumMath.toDegrees(cartographic.latitude);
        const longitude = CesiumMath.toDegrees(cartographic.longitude);

        minLongitude = Math.min(minLongitude, longitude);
        maxLongitude = Math.max(maxLongitude, longitude);
        minLatitude = Math.min(minLatitude, latitude);
        maxLatitude = Math.max(maxLatitude, latitude);
      }
    }

    const bbox = Rectangle.fromDegrees(
      minLongitude,
      minLatitude,
      maxLongitude,
      maxLatitude
    );

    const topleftCart3 = Cartesian3.fromRadians(bbox.west, bbox.north);
    const bottomleftCart3 = Cartesian3.fromRadians(bbox.west, bbox.south);
    const toprightCart3 = Cartesian3.fromRadians(bbox.east, bbox.north);

    // bbox width, height
    const height = Cartesian3.distance(topleftCart3, bottomleftCart3);
    const width = Cartesian3.distance(topleftCart3, toprightCart3);
    const area = width * height;

    return Math.sqrt(area) * 8;
  },

  /**
   * Property 값 추출
   * @private
   * @param {Property} property
   * @param {JulianDate} time
   */
  getPropertyValue(property, time = new JulianDate()) {
    let result;
    if (defined(property)) {
      if (defined(property.getValue)) {
        result = property.getValue(time);
      } else {
        result = property;
      }
    }

    return result;
  },

  /**
   * PositionProperty 생성 함수
   * @param {string} name
   * @returns {object} {
   *  configurable: () => any,
   *  get: () => any,
   *  set: set
   * }
   */
  createPositionPropertyDescriptor(name) {
    const createConstantPositionProperty = (value) => {
      return new ConstantPositionProperty(value);
    };

    return createPropertyDescriptor(
      name,
      undefined,
      createConstantPositionProperty
    );
  },

  /**
   * 특정 요소를 다른 특정 요소 앞으로 위치시키는 함수
   * @private
   */
  moveElementBefore(elementToMove, targetElement) {
    // targetElement의 부모 노드를 찾는다.
    const parent = targetElement.parentNode;

    // elementToMove를 targetElement 앞에 삽입한다.
    parent.insertBefore(elementToMove, targetElement);
  },

  /**
   * Cartesian min, max 계산
   * @param {Cartesian3[]} catesians
   * @returns {Cartesian3[]} catesians [minimum, maximum]
   */
  computeMinMaxCartesian(catesians) {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const cartesian of catesians) {
      // X
      if (cartesian.x < minX) {
        minX = cartesian.x;
      }
      if (maxX < cartesian.x) {
        maxX = cartesian.x;
      }

      // Y
      if (cartesian.y < minY) {
        minY = cartesian.y;
      }
      if (maxY < cartesian.y) {
        maxY = cartesian.y;
      }

      // Z
      if (cartesian.z < minZ) {
        minZ = cartesian.z;
      }
      if (maxZ < cartesian.z) {
        maxZ = cartesian.z;
      }
    }

    const min = new Cartesian3(minX, minY, minZ);
    const max = new Cartesian3(maxX, maxY, maxZ);

    return [min, max];
  },

  setDitapEntityDisplayCondition(ditapEntity, maxValue) {
    if (ditapEntity.pointEntity) {
      ditapEntity.pointEntity.point.distanceDisplayCondition = new DistanceDisplayCondition(
        0,
        maxValue
      );
    }

    if (ditapEntity.pointEntities) {
      ditapEntity.pointEntities.forEach((pointEntity) => {
        pointEntity.point.distanceDisplayCondition = new DistanceDisplayCondition(
          0,
          maxValue
        );
      });
    }

    if (ditapEntity.horizontalPolylineEntity) {
      ditapEntity.horizontalPolylineEntity.polyline.distanceDisplayCondition = new DistanceDisplayCondition(
        0,
        maxValue
      );
    }

    if (ditapEntity.verticalPolylineEntity) {
      ditapEntity.verticalPolylineEntity.polyline.distanceDisplayCondition = new DistanceDisplayCondition(
        0,
        maxValue
      );
    }

    if (ditapEntity.polylineEntity) {
      ditapEntity.polylineEntity.polyline.distanceDisplayCondition = new DistanceDisplayCondition(
        0,
        maxValue
      );
    }

    if (ditapEntity.polylineEntities) {
      ditapEntity.polylineEntities.forEach((polylineEntity) => {
        polylineEntity.polyline.distanceDisplayCondition = new DistanceDisplayCondition(
          0,
          maxValue
        );
      });
    }

    if (ditapEntity.polygonPrimitive) {
      const attributes = ditapEntity.polygonPrimitive.getGeometryInstanceAttributes(
        ditapEntity.polygonPrimitive.geometryInstances.id
      );

      attributes.distanceDisplayCondition = DistanceDisplayConditionGeometryInstanceAttribute.toValue(
        new DistanceDisplayCondition(0, maxValue),
        attributes.distanceDisplayCondition
      );
    }
  },
};

export default Utils;
